import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { of, Observable, catchError, map, take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { ImageType, Product } from '@spartacus/core';

import { BrandsEnum, IMedia, IProductCard, IPushCard, ITag, ThemesEnum } from '@seb/web-design-system/core';
import { IOverview } from '@seb/web-design-system/overview';

import { BrandsCorner, Bundle, ReinsuranceFooterModuleInterface } from '../models';
import { UrlMappingService } from '../services/url-mapping.service';
import { BaseStoreService } from '../services/base-store.service';
import { StockService } from '../services/stock.service';
import { ExternalLogisticianEnum } from '../enums';
import { LinkMapper } from './link.mapper';
import { HobMapper } from './hob.mapper';

@Injectable({
  providedIn: 'root'
})
export class BundleMapper {
  constructor(
    private translateService: TranslateService,
    private baseStoreService: BaseStoreService,
    private urlMappingService: UrlMappingService,
    private linkMapper: LinkMapper,
    private readonly hobMapper: HobMapper,
    private readonly stockService: StockService,
    @Inject(PLATFORM_ID) protected platformId: any
  ) {}

  productsToIPushCards(products: Product[], isTaxesIncluded?: boolean): IPushCard[] {
    if (!products) {
      return undefined;
    }
    return products.map((product) => {
      const hasImagePrimaryThumbnail =
        !!product.images && !!product.images[ImageType.PRIMARY] && !!product.images[ImageType.PRIMARY]['thumbnail'];
      const image: IMedia = hasImagePrimaryThumbnail ? product.images[ImageType.PRIMARY]['thumbnail'] : undefined;
      let subtitle = isTaxesIncluded
        ? product.price?.formattedValue +
          '<br><span class="is-caption is-medium">' +
          this.translateService.instant('product.taxesIncluded') +
          '</span>'
        : product.price?.formattedValue;
      if (product?.productGenre === 'FREE_GIFT' && product?.price?.value === 0) {
        subtitle = this.translateService.instant('product.free.gift');
      }
      return {
        richParagraph: {
          heading: {
            data: {
              title: product.name,
              subtitle: subtitle
            }
          },
          links: [{ url: product.url }]
        },
        media: image,
        indexFollow: product?.indexFollow
      };
    });
  }

  bundleToIProductCustomer(bundle: Bundle): Observable<IProductCard> {
    const mainProduct = bundle?.products?.length > 0 ? bundle?.products[0] : null;
    const productUrl = LinkMapper.decodeUrl(mainProduct?.url);
    const brandFound = mainProduct ? this.baseStoreService.getBrand(mainProduct?.brand) : undefined;
    const hasDiscountedPrice =
      !!bundle.price?.value && bundle.discountedPrice?.value
        ? bundle.discountedPrice?.value !== bundle.price?.value
        : bundle.discountedPrice?.formattedValue !== bundle.price?.formattedValue;

    return this.urlMappingService.getUrlMappingByCode(productUrl)
      .pipe(
        take(1),
        catchError(() => of(null)),
        map((urlMapping) => {
          const baseStore = this.baseStoreService.baseStore;
          const isReviewActivated = baseStore?.ratingAndReviewsIdWebsite;
          const _activeBrands = baseStore?.houseOfBrandConfig?.brand || [];
          const isHob = _activeBrands?.length > 0;
          let finalProductUrl = productUrl;
          if (urlMapping) {
            finalProductUrl = productUrl.replace(urlMapping.code, urlMapping.value);
          }
          return {
            code: bundle?.id,
            title: bundle?.name,
            price: bundle.price?.value ? bundle?.price?.formattedValue : '',
            discountedPrice:
              !!bundle?.discountedPrice && hasDiscountedPrice ? bundle?.discountedPrice?.formattedValue : undefined,
            media: bundle?.galleryImages?.[ImageType.GALLERY]?.[0]?.['main'],
            subtitle: bundle?.claim,
            stock: {
              online: bundle?.stock > 0,
              lowStock: this.stockService.isBundleLowStock(bundle?.products)
            },
            url: bundle?.canonicalURL,
            onSale: true,
            ecoTax: bundle.ecoTaxPrice?.value > 0 ? bundle.ecoTaxPrice.formattedValue : undefined,
            tags: this.getBundleTags(bundle, isHob),
            indexFollow: true,
            brand: brandFound ? mainProduct?.brand : BrandsEnum.default,
            brandLogo: brandFound ? { media: brandFound?.logo } : null,
            brandLogoTitle: isHob ? this.hobMapper.getBrandLogoTitle(mainProduct?.brand) : null,
            isTaxesIncluded: bundle?.isTaxesIncluded,
            ratings: isReviewActivated
              ? {
                averageRatings: mainProduct?.gsebAverageRating
                  ? Number(Number(mainProduct?.gsebAverageRating).toFixed(1))
                  : null,
                numberOfReviews: mainProduct?.gsebNumberOfReviews,
                url: finalProductUrl + '?bundle=' + bundle?.id + '#block-product-reviews'
              }
              : null
          };
        })
      );
  }

  bundleToIOverview(
    bundle: Bundle,
    brandFound?: BrandsCorner,
    isTaxesIncluded?: boolean,
    cmpReinsurancesServicesAndWarranty?: ReinsuranceFooterModuleInterface,
    isHob?: boolean
  ): IOverview {
    const hasDiscountedPrice =
      !!bundle.price?.value && bundle.discountedPrice?.value
        ? bundle.discountedPrice?.value !== bundle.price?.value
        : bundle.discountedPrice?.formattedValue !== bundle.price?.formattedValue;
    const mainProduct = bundle?.products[0];
    const _reinsurancesServicesPdp = cmpReinsurancesServicesAndWarranty?.componentsObjects?.length > 0
      ? this.getReinsurancesServicesPdp(cmpReinsurancesServicesAndWarranty.componentsObjects)
      : null;
    const _reinsurancesPdp = this.getReinsurancesPdp(mainProduct, _reinsurancesServicesPdp);
    return {
      title: bundle?.name,
      subtitle: bundle?.claim,
      tags: this.getBundleTags(bundle, isHob),
      price: bundle.price?.formattedValue,
      discountedPrice:
        !!bundle?.discountedPrice && hasDiscountedPrice ? bundle?.discountedPrice?.formattedValue : undefined,
      ecoTax: bundle.ecoTaxPrice?.value > 0 ? bundle.ecoTaxPrice.formattedValue : undefined,
      stock: {
        online: this.stockMapper(bundle),
        store: this.stockMapper(bundle),
        lowStock: this.stockService.isBundleLowStock(bundle?.products)
      },
      quantity: 0,
      features: bundle?.products[0].benefitHighLights || [],
      onSale: bundle?.products.every((p) => !!p && p.onSale === true && !!p.price),
      // eslint-disable-next-line no-unsafe-optional-chaining
      maxQty: Math.min(...bundle.products?.map((product) => product.maxOrderQuantity)),
      dividedData: bundle.dividedData,
      brandLogo: brandFound ? { media: brandFound?.logo } : null,
      isTaxesIncluded: isTaxesIncluded,
      reinsurancesPdp: _reinsurancesPdp?.length > 0 ? _reinsurancesPdp : null,
      soldBy: mainProduct?.isAccessory
        ? mainProduct?.externalLogistician === ExternalLogisticianEnum.PFS
          ? this.translateService.instant('accessory.pfs.soldBy')
          : this.translateService.instant('accessory.soldBy')
        : mainProduct?.externalLogistician === ExternalLogisticianEnum.PFS
          ? this.translateService.instant('product.pfs.soldBy')
          : this.translateService.instant('product.soldBy'),
      shipping: mainProduct?.isAccessory
        ? this.translateService.instant('accessory.shipping')
        : this.translateService.instant('product.shipping'),
      productId: mainProduct.commercialCode.slice(0, 5),
      promoFreeGift: this.getFreeGiftPromo(bundle)
    };
  }

  getFreeGiftPromo(bundle) {
    const promoFreeGift = [];
    bundle.products.forEach((product) => {
      if (product?.productGenre === 'FREE_GIFT' && product?.price?.value === 0) {
        promoFreeGift.push({
          promo: this.translateService.instant('product.free.gift'),
          text: product.name
        });
      }
    });
    return promoFreeGift;
  }

  private getReinsurancesServicesPdp(reinsurances) {
    const reinsurancesServicesPdp = [];
    reinsurances.forEach((reinsurance) => {
      const splitHeadline = reinsurance.headline.split('</b>');
      const title = splitHeadline[0] + '</b>';
      const subtitle = splitHeadline[1];
      const iconName = reinsurance.ReinsuranceType.toLowerCase();

      reinsurancesServicesPdp.push({
        title,
        info: subtitle,
        icon: { url: `/assets/images/${iconName}.png` },
        description: '',
        type: reinsurance.ReinsuranceType
      });
    });

    return reinsurancesServicesPdp;
  }

  getReinsurancesPdp(product: Product, reinsurancesServicesPdp) {
    const _reinsurancesPdp = [];
    const isForfait = product.productGenre === 'Forfait';
    _reinsurancesPdp.push(this.setReinsurancePdp('', 'securedPayement', false));

    if (!isForfait) {
      const pfs = (product.externalLogistician === ExternalLogisticianEnum.PFS) ? '.pfs' : '';
      _reinsurancesPdp.push(this.setReinsurancePdp(pfs, 'freeDelivery', product.isAccessory));
      _reinsurancesPdp.push(this.setReinsurancePdp('', 'freeReturn', product.isAccessory));
    }

    if (reinsurancesServicesPdp) {
      reinsurancesServicesPdp.forEach((reinsuranceServicesPdp) => _reinsurancesPdp.push(reinsuranceServicesPdp));
    }

    return _reinsurancesPdp.filter((reinsurancePdp) => reinsurancePdp);
  }

  setReinsurancePdp(pfs, reinsurance, isAccessory) {
    let reinsurancePdp = null;
    const prefix = isAccessory ? 'accessory' : 'product';
    const objTranslations = this.translateService.instant([
      `${prefix}.reinsurance.${reinsurance}.title`,
      `${prefix}.reinsurance.${reinsurance}.icon`,
      `${prefix}${pfs}.reinsurance.${reinsurance}.info`,
      `${prefix}.reinsurance.${reinsurance}.descriptionModal`
    ]);

    if (objTranslations) {
      reinsurancePdp = {
        title:
          objTranslations[prefix + '.reinsurance.' + reinsurance + '.title'] &&
          objTranslations[prefix + '.reinsurance.' + reinsurance + '.title'] !==
            prefix + '.reinsurance.' + reinsurance + '.title'
            ? objTranslations[prefix + '.reinsurance.' + reinsurance + '.title']
            : null,
        icon:
          objTranslations[prefix + '.reinsurance.' + reinsurance + '.icon'] &&
          objTranslations[prefix + '.reinsurance.' + reinsurance + '.icon'] !==
            prefix + '.reinsurance.' + reinsurance + '.icon'
            ? {
              url: objTranslations[prefix + '.reinsurance.' + reinsurance + '.icon']
            }
            : null,
        info:
          objTranslations[prefix + pfs + '.reinsurance.' + reinsurance + '.info'] &&
          objTranslations[prefix + pfs + '.reinsurance.' + reinsurance + '.info'] !==
            prefix + pfs + '.reinsurance.' + reinsurance + '.info'
            ? objTranslations[prefix + pfs + '.reinsurance.' + reinsurance + '.info']
            : null,
        description:
          objTranslations[prefix + '.reinsurance.' + reinsurance + '.descriptionModal'] &&
          objTranslations[prefix + '.reinsurance.' + reinsurance + '.descriptionModal'] !==
            prefix + '.reinsurance.' + reinsurance + '.descriptionModal'
            ? objTranslations[prefix + '.reinsurance.' + reinsurance + '.descriptionModal']
            : null,
        type: reinsurance
      };
    }

    return reinsurancePdp?.title ? reinsurancePdp : null;
  }

  stockMapper(bundle: Bundle): boolean {
    return bundle.stock > 0;
  }

  getBundleTags(bundle: Bundle, isHob?: boolean): ITag[] {
    let tags: ITag[] = [{ text: this.translateService.instant('bdp.tag.pack'), theme: isHob ? ThemesEnum.promo : ThemesEnum.default, isDark: isHob }];
    if (bundle.products?.length > 0 && bundle.products[0].bestSeller) {
      tags.push({ text: this.translateService.instant('bdp.tag.bestSeller'), theme: ThemesEnum.promo, isDark: isHob });
    }
    if (bundle.saved && bundle.saved.value > 0) {
      tags.push({ text: '-' + bundle.saved?.formattedValue, theme: ThemesEnum.promo2 });
    }
    if (bundle.discountedPrice?.value !== bundle.price?.value) {
      tags.push({ text: this.translateService.instant('bdp.tag.promotion'), theme: isHob ? ThemesEnum.promo : ThemesEnum.extra, isDark: isHob });
    }
    if (tags.length > 3) {
      tags = tags.slice(0, 3);
    }
    return tags;
  }
}
